/**
 * ViewModel for BrandedEmbeddablePlaylist.
 *
 * Owns:
 *   1. Playlist fetch (siloed — does not share EmbeddablePlaylistViewModel).
 *   2. Sponsor resolution (server-driven via partner-api / Ghostface; the
 *      iframe falls back to the Allstar self-takeover when the server
 *      returns no campaign).
 *   3. Per-clip ad-slot state machine: intro → midclip → endcard.
 *   4. Auto-advance to next clip when endcard countdown completes.
 *
 * Intentionally minimal — we want a clean slate for branded behavior, not
 * inherited complexity from the partner-shared playlist code.
 */
import { useCallback, useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import type { PlaylistClip } from "../../@types";
import type { PlaybackPhase, ResolvedSponsorConfig } from "./@types";
import {
  BrandedActiveClipIndexState,
  BrandedActiveClipState,
  BrandedLayoutVariantState,
  BrandedPlaybackElapsedMsState,
  BrandedPlaybackPhaseState,
  BrandedPlaylistClipsState,
  BrandedPlaylistLoadingState,
  BrandedSponsorConfigState,
  BrandedTopBannerDismissedState,
  BrandedViewerGeoState,
} from "./State";
import { ALLSTAR_FALLBACK_SPONSOR_CONFIG } from "./sponsors";
import { resolveGeoFromWhereami } from "./whereami";
import { decorateSponsorLinks } from "./decorateSponsorLinks";

interface UseBrandedPlaylistArgs {
  playlist: string;
  platform: string;
}

const INTRO_DURATION_MS = 5000;

export const useBrandedPlaylist = ({
  playlist,
  platform,
}: UseBrandedPlaylistArgs) => {
  const setClips = useSetRecoilState(BrandedPlaylistClipsState);
  const setLoading = useSetRecoilState(BrandedPlaylistLoadingState);
  const [activeClip, setActiveClip] = useRecoilState(BrandedActiveClipState);
  const setActiveIndex = useSetRecoilState(BrandedActiveClipIndexState);
  const setSponsor = useSetRecoilState(BrandedSponsorConfigState);
  const [, setGeo] = useRecoilState(BrandedViewerGeoState);
  const setLayoutVariant = useSetRecoilState(BrandedLayoutVariantState);

  /* Hydrate the layout variant from `?layout=bottomPlaylist` once at
     mount. Plumbed via Recoil rather than a prop so deeply-nested
     styled components (PlaylistRow, PlayerFrame, TopBannerSlot,
     PlayerChrome, EndcardOverlay) can pick it up without prop-drilling
     through five intermediate layers. Default — and any unknown
     value — falls through to the side-rail layout. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const layout = params.get("layout");
    if (layout === "bottomPlaylist") setLayoutVariant("bottomPlaylist");
    else setLayoutVariant("sideRail");
  }, [setLayoutVariant]);

  // Resolve viewer geo, then fetch the playlist.
  //
  // Geo resolution order (each level wins over the next):
  //   1. `?geo=DE` URL override — partner integrations and the local
  //      demo page use this to force a specific country without any
  //      backend involvement.
  //   2. `/whereami` (ex-lambdas) — uncached IP-based lookup. Returning
  //      a country here lets us bake `&geo=XX` into the partner-api
  //      playlist URL so the upstream response cache partitions by
  //      country instead of contaminating viewers cross-geo.
  //   3. None → playlist URL omits `&geo=`. partner-api then omits
  //      `adCampaign` rather than IP-resolving inside the cached lambda
  //      (which was the source of the prior cross-geo contamination bug).
  //
  // Both the geo lookup and the playlist fetch share one AbortController
  // so unmount cancels the whole sequence cleanly.
  useEffect(() => {
    if (!playlist) return;
    const params = new URLSearchParams(window.location.search);
    const geoOverride = params.get("geo");
    // ?region=WA override rides along with ?geo= for demo/QA (e.g. the
    // IframeDemo verifying a campaign's state exclusion). Only meaningful
    // when a geo override is present — otherwise both come from /whereami
    // as a pair.
    const regionOverride = params.get("region");
    /* Draft-preview escape hatch. When present, partner-api looks up
       this specific campaign id (regardless of status — draft or
       active) and returns it as the resolved ad campaign for the
       reviewer's geo. Hosted at /iframe-demo for clients to QA their
       drafts before going live. Mongo ObjectId-grade secret (24 hex)
       so unguessable; partner-api validates the id and ignores it if
       the campaign doesn't exist. */
    const previewCampaignId = params.get("previewCampaignId");

    const controller = new AbortController();

    setLoading(true);
    (async () => {
      try {
        const { country: resolvedGeo, region: resolvedRegion } = geoOverride
          ? { country: geoOverride, region: regionOverride }
          : await resolveGeoFromWhereami(controller.signal);
        // Mirror into the atom so anything reading viewer geo from Recoil
        // (e.g. ad-event tracking payloads) sees the same value baked into
        // the playlist URL. Null leaves the atom at its "*" default.
        if (resolvedGeo) setGeo(resolvedGeo);

        const qs = new URLSearchParams({
          name: playlist,
          platform,
          fmt: "iframe",
          // Always ask partner-api for the geo-resolved ad campaign. The
          // iframe no longer carries a local mock catalog — the server
          // (backed by Ghostface) is the single source of truth.
          includeAdCampaign: "1",
        });
        if (resolvedGeo) qs.set("geo", resolvedGeo);
        // State-level campaign exclusion (excludeRegions) — US-market only.
        // `region` is a cache-key param on the playlist endpoint, so
        // forwarding it for every country would fragment the response
        // cache ~50x per geo for no consumer. Absent region on a US viewer
        // is fine: partner-api fails closed on exclude-gated campaigns.
        if (resolvedGeo?.toUpperCase() === "US" && resolvedRegion) {
          qs.set("region", resolvedRegion);
        }
        if (previewCampaignId) qs.set("previewCampaignId", previewCampaignId);

        const url = `${
          process.env.REACT_APP_PARTNER_URL
        }/playlist?${qs.toString()}`;

        const res = await fetch(url, {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_PLAYLIST_API_KEY || "",
          },
        });
        const data = await res.json();
        const clips: PlaylistClip[] = data?.data?.clips ?? [];
        const ready = clips.filter(
          (c) =>
            c.status?.toLowerCase() !== "deleted" &&
            c.status?.toLowerCase() !== "inactive",
        );
        setClips(ready);
        if (ready.length) {
          setActiveClip(ready[0]);
          setActiveIndex(0);
        }
        // Trust the server's geo-resolved campaign. When the server returns
        // no campaign for this geo, fall through to the Allstar self-takeover
        // so the embed never reads as fully unbranded.
        const adCampaign = (data?.data?.adCampaign ??
          null) as ResolvedSponsorConfig | null;
        // Stitch click-throughs on Allstar-owned CTAs to this viewer's
        // RudderStack identity (ajs_aid), mirroring the clip/profile links
        // in HLTVEmbeddablePlaylist. Third-party sponsor URLs pass through
        // untouched — see decorateSponsorLinks.
        setSponsor(
          decorateSponsorLinks(adCampaign ?? ALLSTAR_FALLBACK_SPONSOR_CONFIG),
        );
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        // V1: swallow — empty state will render. Wire toast/log later.
        console.error("BrandedEmbeddablePlaylist: playlist fetch failed", err);
      } finally {
        // Always flip loading off — even on error — so the View can fall
        // through to its empty state instead of spinning forever.
        // AbortError still hits this branch, which is fine: a stale fetch
        // never owned the loading flag for the next mount.
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    playlist,
    platform,
    setClips,
    setLoading,
    setActiveClip,
    setActiveIndex,
    setSponsor,
    setGeo,
  ]);

  // Reset per-clip transient state whenever the active clip changes.
  const setElapsed = useSetRecoilState(BrandedPlaybackElapsedMsState);
  const setPhase = useSetRecoilState(BrandedPlaybackPhaseState);
  const setBannerDismissed = useSetRecoilState(BrandedTopBannerDismissedState);
  useEffect(() => {
    setElapsed(0);
    setPhase("intro");
    setBannerDismissed(false);
  }, [activeClip?._id, setElapsed, setPhase, setBannerDismissed]);
};

/**
 * Per-clip ad-slot phase machine. Components subscribe to
 * BrandedPlaybackPhaseState + BrandedPlaybackElapsedMsState and decide
 * visibility based on those + the sponsor's slot timing config.
 *
 * The intro phase auto-flips to midclip after INTRO_DURATION_MS. The endcard
 * phase is entered when BrandedPlayer's onEnded fires (see ./Components/
 * BrandedPlayer). Auto-advance to next clip happens here when phase === endcard
 * and the configured autoAdvanceMs has elapsed.
 */
export const useAdSlotPhaseMachine = () => {
  const elapsed = useRecoilValue(BrandedPlaybackElapsedMsState);
  const [phase, setPhase] = useRecoilState(BrandedPlaybackPhaseState);
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const clips = useRecoilValue(BrandedPlaylistClipsState);
  const [activeIndex, setActiveIndex] = useRecoilState(
    BrandedActiveClipIndexState,
  );
  const setActiveClip = useSetRecoilState(BrandedActiveClipState);

  // intro -> midclip after INTRO_DURATION_MS of playback.
  useEffect(() => {
    if (phase !== "intro") return;
    if (elapsed >= INTRO_DURATION_MS) setPhase("midclip");
  }, [phase, elapsed, setPhase]);

  // Auto-advance to next clip after the endcard's autoAdvanceMs holds.
  const endcardEnteredAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (phase !== "endcard") {
      endcardEnteredAtRef.current = null;
      return;
    }
    if (endcardEnteredAtRef.current === null) {
      endcardEnteredAtRef.current = Date.now();
    }
    const holdMs = sponsor?.slots.endcard?.autoAdvanceMs ?? 5000;
    const t = window.setTimeout(() => {
      if (!clips?.length) return;
      const nextIndex = (activeIndex + 1) % clips.length;
      setActiveIndex(nextIndex);
      setActiveClip(clips[nextIndex]);
    }, holdMs);
    return () => window.clearTimeout(t);
  }, [
    phase,
    sponsor?.slots.endcard?.autoAdvanceMs,
    clips,
    activeIndex,
    setActiveIndex,
    setActiveClip,
  ]);
};

/** Handlers exposed for the BrandedPlayer to drive playback events. */
export const usePlaybackHandlers = () => {
  const setElapsed = useSetRecoilState(BrandedPlaybackElapsedMsState);
  const setPhase = useSetRecoilState(BrandedPlaybackPhaseState);

  const onTimeUpdate = useCallback(
    (currentTimeSec: number) => setElapsed(currentTimeSec * 1000),
    [setElapsed],
  );

  const onEnded = useCallback(() => setPhase("endcard"), [setPhase]);

  return { onTimeUpdate, onEnded };
};

/** Computed: is the top banner currently visible per its timing config? */
export const computeTopBannerVisible = (
  elapsedMs: number,
  phase: PlaybackPhase,
  appearAfterMs: number | undefined,
  dismissed: boolean,
): boolean => {
  if (dismissed) return false;
  if (phase !== "midclip") return false;
  if (appearAfterMs == null) return false;
  return elapsedMs >= appearAfterMs;
};

/** Computed: should the top banner show its dismiss X yet? */
export const computeTopBannerDismissReady = (
  elapsedMs: number,
  appearAfterMs: number | undefined,
  dismissTimeoutMs: number | undefined,
): boolean => {
  if (appearAfterMs == null) return false;
  const showAt = appearAfterMs + (dismissTimeoutMs ?? 0);
  return elapsedMs >= showAt;
};
